<template>
    <div id="FormMultipleSelection">
        <div :class="`form-multiple-selection multiple-selection-align-${align}`">
            <input
                type="text"
                class="form-multiple-selection-title"
                v-model="localFieldLabel"
                @input="debouncedUpdateField"
                @blur="updateField"
            >
            <div>
                <div v-for="(option, index) in multipleOptions" :key="index"
                     class="form-multiple-selection-option">
                    <input type="checkbox" :id="'multiple-option-' + index" :value="option"
                           v-model="selectedMultipleOptions" @change="updateField" @blur="updateField">
                    <label :for="'multiple-option-' + index">{{ option }}</label>
                    <button @click="removeMultipleOption(index)" class="remove-option">X</button>
                </div>

                <div class="form-multiple-selection-other-option">
                    <input type="text" v-model="localOtherOption" class="form-multiple-selection-other-input"
                           placeholder="Diğer" @input="updateField">
                    <button @click="saveOtherOption" class="form-multiple-selection-save-other">✔</button>
                    <button @click="discardOtherOption" class="form-multiple-selection-discard-other">✖</button>
                </div>

                <input type="text" v-model="newMultipleOption" class="form-multiple-selection-new-option"
                       placeholder="Bir seçenek yazın" @input="updateField" @blur="addMultipleOption">
                <button @click="addMultipleOption" class="form-multiple-selection-add-button">Seçenek Ekle</button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions } from "vuex";
import { debounce } from "lodash";

export default {
    name: "FormMultipleSelection",
    props: {
        fieldLabel: String,
        align: String,
        isRequired: Boolean,
        item: Object,
        otherOption: String,
    },
    data() {
        return {
            localFieldLabel: this.fieldLabel || "",
            multipleOptions: [],
            selectedMultipleOptions: [],
            newMultipleOption: "",
            localOtherOption: this.otherOption || "",
            updateTimer: null,
        };
    },
    methods: {
        ...mapActions("forms", ["updateFormField"]),
        addMultipleOption() {
            if (this.newMultipleOption.trim() !== "") {
                this.multipleOptions.push(this.newMultipleOption.trim());
                this.newMultipleOption = "";
                this.updateField();
            }
        },
        saveOtherOption() {
            if (this.localOtherOption.trim() !== "") {
                this.$message.success("Diğer seçenek kaydedildi.");
                this.updateField();
            }
        },
        discardOtherOption() {
            this.localOtherOption = "";
            this.$message.info("Diğer seçenek iptal edildi.");
            this.updateField();
        },
        removeMultipleOption(index) {
            this.multipleOptions.splice(index, 1);
            this.updateField();
        },
        updateField() {
            let self = this;
            if (this.updateTimer) {
                clearTimeout(this.updateTimer);
            }
            this.updateTimer = setTimeout(function () {
                const updateFields = {
                    multipleSelection: {
                        fieldLabel: self.localFieldLabel,
                        align: self.align,
                        isRequired: self.isRequired,
                        items: self.multipleOptions,
                        otherOption: self.localOtherOption,
                    },
                };
                self.updateFormField({
                    siteId: self.item.siteId,
                    formFieldId: self.item._id,
                    updateFields: updateFields,
                });
                self.$emit("update-field", updateFields);
            }, 200);
        },
    },
    watch: {
        item: {
            handler(newItem) {
                if (newItem && newItem.multipleSelection) {
                    if (newItem.multipleSelection.items) {
                        this.multipleOptions = newItem.multipleSelection.items;
                    }
                    if (newItem.multipleSelection.otherOption) {
                        this.localOtherOption = newItem.multipleSelection.otherOption;
                    }
                }
            },
            immediate: true,
            deep: true,
        },
        fieldLabel: {
            handler(newValue) {
                this.localFieldLabel = newValue || "";
            },
            immediate: true
        },
        otherOption: {
            handler(newValue) {
                this.localOtherOption = newValue;
            },
            immediate: true
        }
    },
    created() {
        this.debouncedUpdateField = debounce(this.updateField, 500);
        if (this.item && this.item.multipleSelection && this.item.multipleSelection.items) {
            this.multipleOptions = this.item.multipleSelection.items;
        }
        this.updateField();
        if (this.item && this.item.multipleSelection) {
            if (this.item.multipleSelection.items) {
                this.multipleOptions = this.item.multipleSelection.items;
            }
            if (this.item.multipleSelection.otherOption) {
                this.localOtherOption = this.item.multipleSelection.otherOption;
            }
        }
    },
};
</script>

<style lang="scss" scoped>
#FormMultipleSelection {
    .form-multiple-selection {
        margin: clamp(0.5rem, 1.5vw, 1rem) auto;
        padding: clamp(0.625rem, 1.5vw, 0.875rem);
        max-width: 700px;
        width: 100%;
        box-sizing: border-box;
        background: linear-gradient(145deg, 
            rgba(255, 255, 255, 0.02) 0%,
            rgba(255, 255, 255, 0.01) 100%);
        border-radius: 0.75rem;
        border: 1px solid rgba(255, 255, 255, 0.06);
        transition: all 0.3s ease;
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;

        &:hover {
            background: linear-gradient(145deg, 
                rgba(255, 255, 255, 0.03) 0%,
                rgba(255, 255, 255, 0.02) 100%);
        }

        .form-multiple-selection-title {
            display: block;
            font-size: clamp(0.95rem, 2.2vw, 1.1rem);
            font-weight: 600;
            margin-bottom: clamp(0.5rem, 1.2vw, 0.75rem);
            color: rgba(55, 65, 81, 0.9);
            border: none;
            padding: 0;
            width: 100%;
            background: transparent;
            outline: none;
            border-bottom: 2px solid transparent;
            transition: border-color 0.2s ease;

            &:focus {
                border-bottom-color: #f26a52;
            }
        }

        .form-multiple-selection-option {
            display: flex;
            align-items: center;
            margin-bottom: clamp(0.5rem, 1.2vw, 0.75rem);
            padding: clamp(0.375rem, 1vw, 0.5rem);
            background: rgba(255, 255, 255, 0.6);
            border: 1px solid rgba(226, 232, 240, 0.8);
            border-radius: 0.375rem;
            transition: all 0.2s ease;

            &:hover {
                background: rgba(255, 255, 255, 0.8);
                border-color: rgba(203, 213, 225, 0.8);
            }

            input[type="checkbox"] {
                margin-right: clamp(0.5rem, 1.2vw, 0.75rem);
                accent-color: #f26a52;
            }

            label {
                flex-grow: 1;
                margin-bottom: 0 !important;
                font-size: clamp(0.875rem, 1.8vw, 0.9rem);
                color: rgba(55, 65, 81, 0.9);
            }

            .remove-option {
                background: #ef4444;
                color: white;
                border: none;
                border-radius: 0.25rem;
                padding: clamp(0.25rem, 0.6vw, 0.375rem) clamp(0.375rem, 0.8vw, 0.5rem);
                cursor: pointer;
                font-size: clamp(0.75rem, 1.4vw, 0.8rem);
                transition: all 0.2s ease;

                &:hover {
                    background: #dc2626;
                    transform: scale(1.05);
                }
            }
        }

        .form-multiple-selection-new-option {
            width: 100%;
            min-width: 0;
            padding: clamp(0.5rem, 1.2vw, 0.75rem);
            border: 1px solid rgba(209, 213, 219, 0.6);
            border-radius: 0.375rem;
            font-size: clamp(0.875rem, 1.8vw, 0.9rem);
            background: rgba(255, 255, 255, 0.8);
            color: rgba(55, 65, 81, 0.9);
            margin-bottom: clamp(0.5rem, 1.2vw, 0.75rem);
            backdrop-filter: blur(10px);
            transition: all 0.3s ease;
            font-family: inherit;
            box-sizing: border-box;

            &:focus {
                outline: none;
                border-color: #f26a52;
                box-shadow: 0 0 0 3px rgba(242, 106, 82, 0.1);
                background: rgba(255, 255, 255, 0.95);
            }

            &::placeholder {
                color: rgba(107, 114, 128, 0.6);
            }
        }

        .form-multiple-selection-add-button {
            padding: clamp(0.5rem, 1.2vw, 0.625rem) clamp(0.75rem, 1.5vw, 1rem);
            background-color: #f26a52;
            color: white;
            border: none;
            border-radius: 0.375rem;
            font-size: clamp(0.8rem, 1.6vw, 0.875rem);
            font-weight: 500;
            cursor: pointer;
            margin-bottom: clamp(0.5rem, 1.2vw, 0.75rem);
            transition: all 0.2s ease;

            &:hover {
                background-color: #e55a42;
                transform: translateY(-1px);
                box-shadow: 0 2px 8px rgba(242, 106, 82, 0.3);
            }
        }

        .form-multiple-selection-other-option {
            display: flex;
            align-items: center;
            gap: clamp(0.375rem, 1vw, 0.5rem);
            margin-top: clamp(0.5rem, 1.2vw, 0.75rem);

            .form-multiple-selection-other-input {
                flex-grow: 1;
                min-width: 0;
                padding: clamp(0.5rem, 1.2vw, 0.75rem);
                border: 1px solid rgba(209, 213, 219, 0.6);
                border-radius: 0.375rem;
                font-size: clamp(0.875rem, 1.8vw, 0.9rem);
                background: rgba(255, 255, 255, 0.8);
                color: rgba(55, 65, 81, 0.9);
                font-family: inherit;
                transition: all 0.3s ease;
                box-sizing: border-box;

                &:focus {
                    outline: none;
                    border-color: #f26a52;
                    box-shadow: 0 0 0 2px rgba(242, 106, 82, 0.1);
                    background: rgba(255, 255, 255, 0.95);
                }
            }

            .form-multiple-selection-save-other,
            .form-multiple-selection-discard-other {
                background: none;
                border: none;
                font-size: clamp(1rem, 2vw, 1.125rem);
                cursor: pointer;
                padding: clamp(0.25rem, 0.6vw, 0.375rem);
                border-radius: 0.25rem;
                transition: all 0.2s ease;

                &:hover {
                    transform: scale(1.1);
                }
            }

            .form-multiple-selection-save-other {
                color: #10b981;
                &:hover {
                    background: rgba(16, 185, 129, 0.1);
                }
            }

            .form-multiple-selection-discard-other {
                color: #ef4444;
                &:hover {
                    background: rgba(239, 68, 68, 0.1);
                }
            }
        }
    }

    .multiple-selection-align-left,
    .multiple-selection-align-right {
        display: flex;
        align-items: flex-start;
        gap: clamp(0.75rem, 2vw, 1rem);

        .form-multiple-selection-title {
            min-width: clamp(80px, 15vw, 120px);
            max-width: clamp(120px, 25vw, 180px);
            flex-shrink: 0;
            margin-bottom: 0;
            padding-top: clamp(0.6rem, 1.5vw, 0.75rem);
            word-wrap: break-word;
            hyphens: auto;
        }

        > div {
            flex: 1;
            min-width: 0; // Flexbox overflow fix
        }

        @media (max-width: 1024px) {
            .form-multiple-selection-title {
                min-width: clamp(70px, 12vw, 100px);
                max-width: clamp(100px, 20vw, 140px);
            }
        }

        @media (max-width: 768px) {
            flex-direction: column;
            gap: 0;
            
            .form-multiple-selection-title {
                min-width: auto;
                max-width: none;
                padding-top: 0;
                margin-bottom: clamp(0.5rem, 1.2vw, 0.75rem);
                text-align: left;
            }

            > div {
                width: 100%;
            }
        }

        @media (max-width: 480px) {
            .form-multiple-selection-title {
                font-size: clamp(0.875rem, 2vw, 1rem);
            }
        }
    }

    .multiple-selection-align-right .form-multiple-selection-title {
        text-align: right;
    }

    .multiple-selection-align-left .form-multiple-selection-title {
        text-align: left;
    }
}
</style>
